import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { EtaxAutoSendDryRunResponse } from "../../../types/etax-auto-send.type";
import { numberWithCommas } from "../../../utils";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";

interface RunRoundPreviewDialogProps {
  preview: EtaxAutoSendDryRunResponse | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700}>
      {value}
    </Typography>
  </Box>
);

/**
 * What a manual round would do, before it does it: the cutoff, how many bills would go, how
 * many are held, and every bill that would be skipped with the reasons the gate gives.
 */
export default function RunRoundPreviewDialog({
  preview,
  loading,
  onClose,
  onConfirm,
}: RunRoundPreviewDialogProps) {
  // A round with nothing to send would only write an empty round row; don't offer it.
  const nothingToSend = !preview || preview.would_send_count <= 0;
  return (
    <Dialog
      open={Boolean(preview)}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        รันรอบส่งตอนนี้
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {preview && (
          <>
            {!preview.settings_enabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                ขณะนี้ปิดการส่งอัตโนมัติอยู่ แต่ยังสั่งรันรอบเองได้
              </Alert>
            )}
            <Fact
              label="ตัดรอบ (บิลที่ออกก่อนเวลานี้)"
              value={formatThaiShortDateTimeBkk(preview.cutoff) || "-"}
            />
            <Fact
              label="บิลที่เข้ารอบ"
              value={`${numberWithCommas(preview.candidate_count)} ใบ`}
            />
            <Fact
              label="จะส่ง"
              value={`${numberWithCommas(preview.would_send_count)} ใบ`}
            />
            <Fact
              label="ไม่ส่งอัตโนมัติ (ข้าม)"
              value={`${numberWithCommas(preview.held_count)} ใบ`}
            />
            <Fact
              label="ส่งไม่ได้ (ข้าม)"
              value={`${numberWithCommas(preview.would_skip_count)} ใบ`}
            />

            {nothingToSend && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                ไม่มีบิลที่จะส่งในรอบนี้ จึงยังรันรอบไม่ได้
              </Alert>
            )}

            {preview.would_skip.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  บิลที่จะถูกข้าม
                </Typography>
                <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>เลขที่บิล</TableCell>
                        <TableCell>เหตุผล</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.would_skip.map((skip) => (
                        <TableRow key={skip.transaction_uuid}>
                          <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                            {skip.transaction_no ?? skip.transaction_uuid}
                          </TableCell>
                          <TableCell>
                            {skip.messages.length > 0 ? (
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {skip.messages.map((message) => (
                                  <li key={message}>{message}</li>
                                ))}
                              </Box>
                            ) : (
                              skip.reason_codes.join(", ") || "-"
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          ปิด
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          loading={loading}
          disabled={nothingToSend}
        >
          ยืนยันรันรอบ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
